import { fetchAuthSession } from "aws-amplify/auth";
import { resolveApiFetchUrl } from "./ApiBaseUrl";
import { apiFetch } from "./apiFetch";
import { decryptText, encryptText } from "./CryptoUtil";

function pathForHomeEncryptCheck(url) {
    if (!url || typeof url !== "string") return "";
    let path = url.split("?")[0];
    try {
        if (path.startsWith("http://") || path.startsWith("https://")) {
            path = new URL(path).pathname;
        }
    } catch {
        /* keep path */
    }
    return path;
}

// Encrypt request body for /home PUT/POST when in production or when REACT_APP_ENCRYPT_HOME_REQUESTS is set (for testing)
const shouldEncryptHomeBody = (url) => {
    const path = pathForHomeEncryptCheck(url);
    if (!path.startsWith("/home")) return false;
    return process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENCRYPT_HOME_REQUESTS === 'true';
};

// Log /v1 and /home API calls when REACT_APP_LOG_API is set
const shouldLogApi = (url) =>
    process.env.REACT_APP_LOG_API === 'true' &&
    url &&
    (String(url).startsWith('/v1') || String(url).startsWith('/home'));

const logApi = (method, url, statusOrError) => {
    if (!shouldLogApi(url)) return;
    const fullUrl = resolveApiFetchUrl(url);
    console.log(`[ApiService] ${method} ${fullUrl} → ${statusOrError}`);
};

function extractHtmlTitle(bodyText) {
    if (!bodyText) return "";
    const match = /<title>([^<]{1,200})<\/title>/i.exec(bodyText);
    return match ? match[1].trim() : "";
}

/**
 * Gateways (ALB/nginx) answer with an HTML error page, not JSON, when a long request exceeds their timeout.
 * Blind `response.json()` on that turns into `Unexpected token '<'`, which hides what actually happened.
 */
function nonJsonResponseError(result, bodyText) {
    const title = extractHtmlTitle(bodyText);
    const detail = title || `HTTP ${result.status}${result.statusText ? ` ${result.statusText}` : ""}`;
    const timedOut = result.status === 504 || result.status === 502 || /time-?out/i.test(detail);
    const error = new Error(
        timedOut
            ? `${detail} — the request took too long for the gateway. The server may still be working on it.`
            : `Server returned a non-JSON response (${detail}).`
    );
    error.status = result.status;
    error.bodyText = bodyText ? bodyText.slice(0, 500) : "";
    return error;
}

async function parseJsonResponse(result) {
    const text = await result.text();
    if (!text) {
        if (result.ok) return null;
        throw nonJsonResponseError(result, "");
    }
    try {
        return JSON.parse(text);
    } catch {
        throw nonJsonResponseError(result, text);
    }
}

const ApiService = {
    init: {
        headers: new Headers(),
    },

    getAccessToken: async () => {
        const session = await fetchAuthSession();
        if (!session.tokens) {
            ApiService.init.headers.delete("Authorization");
            return;
        }

        const { idToken, accessToken } = session.tokens;
        const bearer = idToken ?? accessToken;
        if (!bearer) {
            ApiService.init.headers.delete("Authorization");
            return;
        }
        ApiService.init.headers.set("Content-Type", `application/json`);
        ApiService.init.headers.set("Authorization", `Bearer ${bearer.toString()}`);
    },

    getData: async (url, param) => {
        await ApiService.getAccessToken();

        var requestOptions = {
            method: "GET",
            headers: ApiService.init.headers,
        };


        let entities = [];

        let queryString = "";
        if (param) {
            // console.log('ApiService.getData - param received:', param);
            for (const [key, value] of Object.entries(param)) {
                if (value || value == 0) {
                    entities.push(`${key}=${value}`);
                } else {
                    entities.push(`${key}=`);
                }
            }
            queryString = url + "?" + entities.join("&");
            // console.log('ApiService.getData - queryString:', queryString);
        } else {
            queryString = url;
        }

        const result = await apiFetch(resolveApiFetchUrl(queryString), requestOptions);
        logApi('GET', queryString, result.status);
        const json = await parseJsonResponse(result);

        // Check if the response contains encrypted data and decrypt it
        if (json && json.encrypted) {
            const decryptedData = decryptText(json.encrypted);
            if (decryptedData) {
                try {
                    // Try to parse the decrypted data as JSON
                    return JSON.parse(decryptedData);
                } catch (parseError) {
                    console.error('Failed to parse decrypted data as JSON:', parseError);
                    // Return the decrypted string if it's not JSON
                    return decryptedData;
                }
            } else {
                console.error('Failed to decrypt API response');
                return json; // Return original response if decryption fails
            }
        }

        return json;
    },

    putData: async (url, data) => {
        await ApiService.getAccessToken();

        // Ensure Content-Type is always set, even if user is not authenticated
        const headers = new Headers(ApiService.init.headers);
        if (!headers.has("Content-Type")) {
            headers.set("Content-Type", "application/json");
        }

        let body;
        if (shouldEncryptHomeBody(url) && data !== undefined && data !== null) {
            const encrypted = encryptText(JSON.stringify(data));
            body = encrypted ? JSON.stringify({ encrypted }) : JSON.stringify(data);
            if (encrypted && (url.includes('grid-play/progress') || url.includes('sliding-play/progress'))) {
                console.log('[ApiService] Encrypted PUT body for', url);
            }
        } else {
            body = JSON.stringify(data);
        }

        var requestOptions = {
            method: "PUT",
            headers: headers,
            body,
        };

        const result = await apiFetch(resolveApiFetchUrl(url), requestOptions);
        logApi('PUT', url, result.status);
        // Check HTTP status code
        if (!result.ok) {
            const errorData = await result.json().catch(() => ({ message: `HTTP ${result.status}: ${result.statusText}` }));
            const error = new Error(errorData.message || `HTTP ${result.status}: ${result.statusText}`);
            error.status = result.status;
            error.response = errorData;
            throw error;
        }

        const json = await parseJsonResponse(result);

        // Check if the response contains encrypted data and decrypt it
        if (json && json.encrypted) {
            const decryptedData = decryptText(json.encrypted);
            if (decryptedData) {
                try {
                    // Try to parse the decrypted data as JSON
                    return JSON.parse(decryptedData);
                } catch (parseError) {
                    console.error('Failed to parse decrypted data as JSON:', parseError);
                    // Return the decrypted string if it's not JSON
                    return decryptedData;
                }
            } else {
                console.error('Failed to decrypt API response');
                return json; // Return original response if decryption fails
            }
        }

        return json;
    },

    postData: async (url, data) => {
        await ApiService.getAccessToken();

        // Ensure Content-Type is always set, even if user is not authenticated
        const headers = new Headers(ApiService.init.headers);
        if (!headers.has("Content-Type")) {
            headers.set("Content-Type", "application/json");
        }

        let body;
        if (shouldEncryptHomeBody(url) && data !== undefined && data !== null) {
            const encrypted = encryptText(JSON.stringify(data));
            body = encrypted ? JSON.stringify({ encrypted }) : JSON.stringify(data);
        } else {
            body = JSON.stringify(data);
        }

        var requestOptions = {
            method: "POST",
            headers: headers,
            body,
        };

        const result = await apiFetch(resolveApiFetchUrl(url), requestOptions);
        logApi('POST', url, result.status);
        const json = await parseJsonResponse(result);

        // Check if the response contains encrypted data and decrypt it
        if (json && json.encrypted) {
            const decryptedData = decryptText(json.encrypted);
            if (decryptedData) {
                try {
                    // Try to parse the decrypted data as JSON
                    return JSON.parse(decryptedData);
                } catch (parseError) {
                    console.error('Failed to parse decrypted data as JSON:', parseError);
                    // Return the decrypted string if it's not JSON
                    return decryptedData;
                }
            } else {
                console.error('Failed to decrypt API response');
                return json; // Return original response if decryption fails
            }
        }

        return json;
    },

    /**
     * POST for endpoints that allow anonymous callers. Tries to attach Bearer when a session exists;
     * if token resolution throws (e.g. guest / Amplify session errors), sends the request without Authorization.
     */
    postDataOptionalAuth: async (url, data) => {
        const headers = new Headers();
        headers.set("Content-Type", "application/json");
        try {
            await ApiService.getAccessToken();
            const bearer = ApiService.init.headers.get("Authorization");
            if (bearer) {
                headers.set("Authorization", bearer);
            }
        } catch {
            /* guest or transient auth error — omit Authorization */
        }

        let body;
        if (shouldEncryptHomeBody(url) && data !== undefined && data !== null) {
            const encrypted = encryptText(JSON.stringify(data));
            body = encrypted ? JSON.stringify({ encrypted }) : JSON.stringify(data);
        } else {
            body = JSON.stringify(data);
        }

        const result = await apiFetch(resolveApiFetchUrl(url), {
            method: "POST",
            headers,
            body,
        });
        logApi("POST", url, result.status);
        const json = await parseJsonResponse(result);

        if (json && json.encrypted) {
            const decryptedData = decryptText(json.encrypted);
            if (decryptedData) {
                try {
                    return JSON.parse(decryptedData);
                } catch (parseError) {
                    console.error("Failed to parse decrypted data as JSON:", parseError);
                    return decryptedData;
                }
            } else {
                console.error("Failed to decrypt API response");
                return json;
            }
        }

        return json;
    },

    deleteData: async (url, data) => {
        await ApiService.getAccessToken();

        // Ensure Content-Type is always set, even if user is not authenticated
        const headers = new Headers(ApiService.init.headers);
        if (!headers.has("Content-Type")) {
            headers.set("Content-Type", "application/json");
        }

        var requestOptions = {
            method: "DELETE",
            headers: headers,
        };

        if (data) {
            if (shouldEncryptHomeBody(url)) {
                const encrypted = encryptText(JSON.stringify(data));
                requestOptions.body = encrypted ? JSON.stringify({ encrypted }) : JSON.stringify(data);
            } else {
                requestOptions.body = JSON.stringify(data);
            }
        }

        const result = await apiFetch(resolveApiFetchUrl(url), requestOptions);
        logApi('DELETE', url, result.status);
        const json = await result.json().catch(() => ({}));

        if (!result.ok) {
            const error = new Error(
                json.message || json.error || `HTTP ${result.status}: ${result.statusText}`
            );
            error.status = result.status;
            error.response = json;
            throw error;
        }

        if (json && json.encrypted) {
            const decryptedData = decryptText(json.encrypted);
            if (decryptedData) {
                try {
                    return JSON.parse(decryptedData);
                } catch (parseError) {
                    console.error("Failed to parse decrypted DELETE response as JSON:", parseError);
                    return decryptedData;
                }
            }
            console.error("Failed to decrypt DELETE API response");
            return json;
        }

        return json;
    },

    uploadFile: async (url, file) => {
        let promiseResponse = new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append("file", file);

            var xhr = new XMLHttpRequest();
            xhr.open("POST", resolveApiFetchUrl(url), true);
            xhr.setRequestHeader("X-CSRF-TOKEN", ApiService.init.headers.get("X-CSRF-TOKEN"));
            xhr.onload = function () {
                if (this.status == 200) {
                    resolve(this.response);
                } else {
                    reject(this.response);
                }
            };
            xhr.send(formData);
        });

        return promiseResponse;
    },

    uploadHwCapturedImage: async (title, image) => {
        await ApiService.getAccessToken();

        // Hedaer do not inlcudes 'Content-Type' beacuse the browser will set it correctly
        // to 'multipart/form-data' including the boundary
        const headers = {
            Authorization: ApiService.init.headers.get("Authorization"),
        };

        const formData = new FormData();
        formData.append("title", title);
        formData.append("file", image);

        // Make a POST request to the API endpoint with the FormData using fetch
        const result = await fetch(resolveApiFetchUrl("/v1/puzzle/grid/upload"), {
            method: "POST",
            headers: headers,
            body: formData,
        })
            .then((response) => {
                if (response.ok) {
                    return response;
                }
            })
            .catch((error) => {
                // Handle fetch error
                throw new Error(error);
            });

        const jsonResult = await parseJsonResponse(result);

        if (jsonResult && jsonResult.code === "FAILURE") {
            throw jsonResult;
        }

        return result;
    },

    downloadFile: async (url, param) => {
        await ApiService.getAccessToken();


        let queryString = url;
        if (param) {
            const params = Object.entries(param)
                .map(([key, value]) => `${key}=${value || ''}`)
                .join('&');
            queryString += `?${params}`;
        }

        try {
            const response = await fetch(resolveApiFetchUrl(queryString), {
                method: "GET",
                headers: ApiService.init.headers
            });
            
            // Check if response is JSON (error message) or blob (file)
            const contentType = response.headers.get("content-type");
            
            if (contentType && contentType.includes("application/json")) {
                const errorData = await response.json();
                if (errorData.code === "FAILURE") {
                    throw new Error(errorData.message || "Download failed");
                }
                throw new Error("Unexpected JSON response");
            }
            
            if (!response.ok) {
                throw new Error(`Download failed! Status: ${response.status}`);
            }
            
            return await response.blob();
        } catch (error) {
            console.error("Download failed:", error);
            throw error;
        }
    },

    /**
     * POST multipart/form-data (e.g. file uploads with fields).
     * Does NOT set Content-Type — browser sets it with boundary automatically.
     */
    postFormData: async (url, formData) => {
        await ApiService.getAccessToken();
        const authHeader = ApiService.init.headers.get("Authorization");
        const headers = {};
        if (authHeader) headers["Authorization"] = authHeader;
        // Do NOT set Content-Type — browser must set multipart boundary itself

        const response = await fetch(resolveApiFetchUrl(url), {
            method: "POST",
            headers,
            body: formData,
        });
        return await response.json();
    },

    /**
     * PUT multipart/form-data (e.g. replace file + metadata on update).
     * Does NOT set Content-Type — browser sets it with boundary automatically.
     */
    putFormData: async (url, formData) => {
        await ApiService.getAccessToken();
        const authHeader = ApiService.init.headers.get("Authorization");
        const headers = {};
        if (authHeader) headers["Authorization"] = authHeader;

        const response = await fetch(resolveApiFetchUrl(url), {
            method: "PUT",
            headers,
            body: formData,
        });
        return await response.json();
    },
};

export default ApiService;
